<template>
  <div class="admin-licenses">
    <div class="page-heading">
      <div>
        <h1>Licenses</h1>
        <p>Generate, assign, and revoke desktop licenses.</p>
      </div>
      <button class="primary-btn" @click="openGenerateModal">
        <AppIcon name="bi-plus-lg" class="bi bi-plus-lg" />
        Generate licenses
      </button>
    </div>

    <div class="stats-grid">
      <div class="stat-card total"><span>Total</span><strong>{{ stats.total }}</strong></div>
      <div class="stat-card available"><span>Available</span><strong>{{ stats.created }}</strong></div>
      <div class="stat-card active"><span>Activated</span><strong>{{ stats.activated }}</strong></div>
      <div class="stat-card revoked"><span>Revoked</span><strong>{{ stats.deactivated }}</strong></div>
    </div>

    <div class="toolbar">
      <div class="search-box">
        <AppIcon name="bi-search" class="bi bi-search" />
        <input v-model.trim="search" type="search" placeholder="Search key or email..." @input="scheduleSearch">
      </div>
      <select v-model="status" @change="applyFilters">
        <option value="">All statuses</option>
        <option value="created">Available</option>
        <option value="activated">Activated</option>
        <option value="deactivated">Revoked</option>
        <option value="expired">Expired</option>
        <option value="invalid">Invalid</option>
      </select>
    </div>

    <div class="table-card">
      <div class="table-scroll">
        <table>
          <thead>
            <tr>
              <th>License key</th>
              <th>Assigned email</th>
              <th>Status</th>
              <th>Created</th>
              <th>Last updated</th>
              <th class="actions-heading">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="license in licenses" :key="license.id">
              <td>
                <button class="key-value" title="Copy license key" @click="copyText(license.serial_key)">
                  <code>{{ license.serial_key }}</code>
                  <AppIcon name="bi-copy" class="bi bi-copy" />
                </button>
              </td>
              <td>{{ license.email || '—' }}</td>
              <td><span class="status-badge" :class="license.status">{{ statusLabel(license.status) }}</span></td>
              <td>{{ formatDate(license.created_at) }}</td>
              <td>{{ formatDate(license.updated_at) }}</td>
              <td class="actions-cell">
                <button
                  class="revoke-btn"
                  :disabled="license.status === 'deactivated' || revokingId === license.id"
                  @click="revokeLicense(license)"
                >
                  {{ license.status === 'deactivated' ? 'Revoked' : (revokingId === license.id ? 'Revoking...' : 'Revoke') }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="loading" class="empty-state">Loading licenses...</div>
      <div v-else-if="!licenses.length" class="empty-state">
        <AppIcon name="bi-key" class="bi bi-key" />
        <strong>No licenses found</strong>
        <span>Generate a license or change your filters.</span>
      </div>
    </div>

    <div v-if="pagination.totalPages > 1" class="pagination">
      <button :disabled="pagination.page <= 1" @click="changePage(pagination.page - 1)">Previous</button>
      <span>Page {{ pagination.page }} of {{ pagination.totalPages }}</span>
      <button :disabled="pagination.page >= pagination.totalPages" @click="changePage(pagination.page + 1)">Next</button>
    </div>

    <div v-if="showGenerateModal" class="modal-overlay" @click.self="closeGenerateModal">
      <div class="modal-card">
        <div class="modal-header">
          <div>
            <span class="eyebrow">New keys</span>
            <h2>Generate licenses</h2>
          </div>
          <button class="close-btn" aria-label="Close" @click="closeGenerateModal">&times;</button>
        </div>

        <div v-if="!generatedLicenses.length" class="modal-body">
          <label class="field">
            <span>Customer email (optional)</span>
            <input v-model.trim="generateForm.email" type="email" placeholder="customer@example.com">
            <small>When provided, the license can only be activated with this email.</small>
          </label>
          <label class="field">
            <span>Number of licenses</span>
            <input v-model.number="generateForm.count" type="number" min="1" max="100">
          </label>
          <div v-if="generateError" class="form-error">{{ generateError }}</div>
          <div class="modal-actions">
            <button class="secondary-btn" @click="closeGenerateModal">Cancel</button>
            <button class="primary-btn" :disabled="generating || !validCount" @click="generateLicenses">
              {{ generating ? 'Generating...' : 'Generate' }}
            </button>
          </div>
        </div>

        <div v-else class="modal-body generated-body">
          <div class="success-message">
            <AppIcon name="bi-check-circle" class="bi bi-check-circle" />
            <div><strong>{{ generatedLicenses.length }} license{{ generatedLicenses.length === 1 ? '' : 's' }} generated</strong><span>Copy the keys and share them securely.</span></div>
          </div>
          <div class="generated-list">
            <button v-for="license in generatedLicenses" :key="license.id" @click="copyText(license.serial_key)">
              <code>{{ license.serial_key }}</code><AppIcon name="bi-copy" class="bi bi-copy" />
            </button>
          </div>
          <div class="modal-actions">
            <button class="secondary-btn" @click="copyAll">Copy all</button>
            <button class="primary-btn" @click="closeGenerateModal">Done</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import moment from 'moment'

export default {
  name: 'AdminLicenses',
  data() {
    return {
      licenses: [],
      stats: { total: 0, created: 0, activated: 0, deactivated: 0, expired: 0, invalid: 0 },
      pagination: { page: 1, totalPages: 1, total: 0 },
      search: '',
      status: '',
      loading: false,
      revokingId: null,
      showGenerateModal: false,
      generating: false,
      generateError: '',
      generateForm: { email: '', count: 1 },
      generatedLicenses: [],
      searchTimer: null
    }
  },
  computed: {
    validCount() {
      return Number.isInteger(this.generateForm.count) && this.generateForm.count >= 1 && this.generateForm.count <= 100
    }
  },
  created() {
    this.fetchLicenses()
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer)
  },
  methods: {
    async fetchLicenses() {
      this.loading = true
      try {
        const response = await axios.get(`${process.env.VUE_APP_API}/admin/licenses`, {
          params: { page: this.pagination.page, limit: 25, search: this.search, status: this.status }
        })
        this.licenses = response.data.licenses || []
        this.stats = { ...this.stats, ...(response.data.stats || {}) }
        this.pagination = response.data.pagination || this.pagination
      } catch (error) {
        this.$toast.error(error.response?.data?.message || 'Failed to load licenses')
      } finally {
        this.loading = false
      }
    },
    scheduleSearch() {
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => this.applyFilters(), 350)
    },
    applyFilters() {
      this.pagination.page = 1
      this.fetchLicenses()
    },
    changePage(page) {
      this.pagination.page = page
      this.fetchLicenses()
    },
    openGenerateModal() {
      this.generateForm = { email: '', count: 1 }
      this.generatedLicenses = []
      this.generateError = ''
      this.showGenerateModal = true
    },
    closeGenerateModal() {
      this.showGenerateModal = false
      this.generatedLicenses = []
      this.generateError = ''
    },
    async generateLicenses() {
      if (!this.validCount) return
      this.generating = true
      this.generateError = ''
      try {
        const response = await axios.post(`${process.env.VUE_APP_API}/admin/licenses`, this.generateForm)
        this.generatedLicenses = response.data.licenses || []
        this.pagination.page = 1
        await this.fetchLicenses()
      } catch (error) {
        this.generateError = error.response?.data?.message || 'Failed to generate licenses'
      } finally {
        this.generating = false
      }
    },
    async revokeLicense(license) {
      const confirmed = window.confirm(`Revoke ${license.serial_key}? This license will stop passing validation immediately.`)
      if (!confirmed) return
      this.revokingId = license.id
      try {
        const response = await axios.patch(`${process.env.VUE_APP_API}/admin/licenses/${license.id}/revoke`)
        const index = this.licenses.findIndex(item => item.id === license.id)
        if (index >= 0) this.$set(this.licenses, index, response.data.license)
        await this.fetchLicenses()
        this.$toast.success('License revoked')
      } catch (error) {
        this.$toast.error(error.response?.data?.message || 'Failed to revoke license')
      } finally {
        this.revokingId = null
      }
    },
    async copyText(text) {
      try {
        await navigator.clipboard.writeText(text)
        this.$toast.success('Copied to clipboard')
      } catch (_) {
        const input = document.createElement('textarea')
        input.value = text
        document.body.appendChild(input)
        input.select()
        document.execCommand('copy')
        document.body.removeChild(input)
        this.$toast.success('Copied to clipboard')
      }
    },
    copyAll() {
      this.copyText(this.generatedLicenses.map(license => license.serial_key).join('\n'))
    },
    statusLabel(status) {
      return { created: 'Available', activated: 'Activated', deactivated: 'Revoked' }[status] || status
    },
    formatDate(date) {
      return date ? moment(date).format('MMM D, YYYY · HH:mm') : '—'
    }
  }
}
</script>

<style scoped>
.admin-licenses { display: flex; flex-direction: column; gap: 20px; }
.page-heading, .toolbar, .modal-header, .modal-actions, .success-message { align-items: center; display: flex; }
.page-heading { justify-content: space-between; gap: 20px; }
.page-heading h1 { color: #111827; font-size: 24px; margin: 0 0 5px; }
.page-heading p { color: #64748b; font-size: 13px; margin: 0; }
.primary-btn, .secondary-btn, .revoke-btn, .pagination button { border-radius: 9px; cursor: pointer; font-size: 13px; font-weight: 650; }
.primary-btn { align-items: center; background: #4f46e5; border: 0; color: #fff; display: inline-flex; gap: 7px; padding: 10px 15px; }
.primary-btn:disabled { cursor: not-allowed; opacity: .55; }
.secondary-btn { background: #fff; border: 1px solid #dbe3ef; color: #475569; padding: 9px 14px; }
.stats-grid { display: grid; gap: 16px; grid-template-columns: repeat(4, minmax(0, 1fr)); }
.stat-card { background: #fff; border: 1px solid #e5e7eb; border-radius: 12px; display: flex; flex-direction: column; gap: 7px; overflow: hidden; padding: 18px 20px; position: relative; }
.stat-card::before { background: #6366f1; content: ''; inset: 0 auto 0 0; position: absolute; width: 4px; }
.stat-card.available::before { background: #3b82f6; }
.stat-card.active::before { background: #10b981; }
.stat-card.revoked::before { background: #ef4444; }
.stat-card span { color: #64748b; font-size: 12px; font-weight: 650; }
.stat-card strong { color: #111827; font-size: 28px; line-height: 1; }
.toolbar { gap: 12px; justify-content: space-between; }
.search-box { max-width: 360px; position: relative; width: 100%; }
.search-box i { color: #94a3b8; left: 13px; position: absolute; top: 50%; transform: translateY(-50%); }
.search-box input, .toolbar select, .field input { background: #fff; border: 1px solid #dbe3ef; border-radius: 8px; color: #111827; font: inherit; font-size: 13px; outline: none; padding: 10px 12px; }
.search-box input { padding-left: 38px; width: 100%; }
.toolbar select { min-width: 155px; }
.search-box input:focus, .toolbar select:focus, .field input:focus { border-color: #818cf8; box-shadow: 0 0 0 3px rgba(99, 102, 241, .1); }
.table-card { background: #fff; border: 1px solid #e5e7eb; border-radius: 12px; overflow: hidden; }
.table-scroll { overflow-x: auto; }
table { border-collapse: collapse; width: 100%; }
th { background: #f8fafc; border-bottom: 1px solid #e5e7eb; color: #64748b; font-size: 11px; font-weight: 700; letter-spacing: .04em; padding: 13px 16px; text-align: left; text-transform: uppercase; white-space: nowrap; }
td { border-bottom: 1px solid #f1f5f9; color: #475569; font-size: 13px; padding: 14px 16px; white-space: nowrap; }
tbody tr:last-child td { border-bottom: 0; }
tbody tr:hover td { background: #fafbfc; }
.key-value { align-items: center; background: none; border: 0; color: #334155; cursor: pointer; display: flex; gap: 8px; padding: 0; }
.key-value code, .generated-list code { font-family: 'SFMono-Regular', Consolas, monospace; font-size: 12px; font-weight: 650; }
.key-value i { color: #94a3b8; }
.status-badge { border-radius: 999px; display: inline-flex; font-size: 11px; font-weight: 700; padding: 4px 9px; text-transform: capitalize; }
.status-badge.created { background: #eff6ff; color: #2563eb; }
.status-badge.activated { background: #ecfdf5; color: #059669; }
.status-badge.deactivated, .status-badge.invalid { background: #fef2f2; color: #dc2626; }
.status-badge.expired { background: #fff7ed; color: #c2410c; }
.actions-heading, .actions-cell { text-align: right; }
.revoke-btn { background: #fff; border: 1px solid #fecaca; color: #dc2626; padding: 7px 11px; }
.revoke-btn:hover:not(:disabled) { background: #fef2f2; }
.revoke-btn:disabled { border-color: #e5e7eb; color: #94a3b8; cursor: not-allowed; }
.empty-state { align-items: center; color: #64748b; display: flex; flex-direction: column; font-size: 13px; gap: 7px; padding: 50px 20px; }
.empty-state i { color: #a5b4fc; font-size: 27px; }
.empty-state strong { color: #334155; }
.pagination { align-items: center; display: flex; gap: 12px; justify-content: center; }
.pagination button { background: #fff; border: 1px solid #dbe3ef; color: #475569; padding: 8px 12px; }
.pagination button:disabled { cursor: not-allowed; opacity: .5; }
.pagination span { color: #64748b; font-size: 12px; }
.modal-overlay { align-items: center; backdrop-filter: blur(3px); background: rgba(15, 23, 42, .55); display: flex; inset: 0; justify-content: center; padding: 20px; position: fixed; z-index: 100; }
.modal-card { background: #fff; border-radius: 14px; box-shadow: 0 24px 60px rgba(15, 23, 42, .2); max-height: 90vh; max-width: 520px; overflow-y: auto; width: 100%; }
.modal-header { border-bottom: 1px solid #eef2f7; justify-content: space-between; padding: 20px 22px; }
.modal-header h2 { color: #111827; font-size: 17px; margin: 3px 0 0; }
.eyebrow { color: #6366f1; font-size: 10px; font-weight: 800; letter-spacing: .08em; text-transform: uppercase; }
.close-btn { background: none; border: 0; color: #94a3b8; cursor: pointer; font-size: 26px; }
.modal-body { padding: 22px; }
.field { display: flex; flex-direction: column; gap: 7px; margin-bottom: 16px; }
.field > span { color: #475569; font-size: 12px; font-weight: 700; }
.field small { color: #94a3b8; font-size: 11px; }
.form-error { background: #fef2f2; border-radius: 8px; color: #dc2626; font-size: 12px; margin-bottom: 14px; padding: 10px; }
.modal-actions { gap: 10px; justify-content: flex-end; margin-top: 20px; }
.success-message { background: #ecfdf5; border-radius: 9px; color: #047857; gap: 11px; padding: 13px; }
.success-message i { font-size: 20px; }
.success-message div { display: flex; flex-direction: column; gap: 2px; }
.success-message span { font-size: 11px; }
.generated-list { border: 1px solid #e5e7eb; border-radius: 9px; margin-top: 16px; max-height: 300px; overflow-y: auto; }
.generated-list button { align-items: center; background: #fff; border: 0; border-bottom: 1px solid #eef2f7; color: #334155; cursor: pointer; display: flex; justify-content: space-between; padding: 11px 12px; text-align: left; width: 100%; }
.generated-list button:last-child { border-bottom: 0; }
.generated-list button:hover { background: #f8fafc; }
.generated-list i { color: #94a3b8; }
@media (max-width: 700px) {
  .stats-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .page-heading { align-items: stretch; flex-direction: column; }
  .page-heading .primary-btn { justify-content: center; }
  .toolbar { align-items: stretch; flex-direction: column; }
  .search-box { max-width: none; }
  .toolbar select { width: 100%; }
}
</style>
